import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../contexts/app.context';
import Input from '../input';
import {
  arrivalTimes,
  classLevels,
  departureTimes,
  modesOfTransport,
  NUMBER_OF_TOUR_STUDENTS,
  tourTypes,
  visitDays,
} from './data';
import Text from '../text';
import TimeSlotsForm from './timeSlots';
import { scrollToClass } from './util';
import { OpenIcon } from '../../assets/icons';
import { BiTrash } from 'react-icons/bi';
import Popup from '../popup';

/**
 * @param {
 *  type: school | other-school | other-group,
 *  objectName: string,
 *  formik: formik instance,
 *  translationPrefix: string,
 *  openClassIndex: number,
 *  experienceInteractionConfig: {
 *    visible: boolean,
 *    editable: boolean,
 *  }
 * } props
 * @returns
 */
const ClassDetailsForm = ({
  type = 'school',
  objectName = 'classes',
  formik,
  translationPrefix,
  openClassIndex,
  experienceInteractionConfig,
}) => {
  const { t } = useTranslation();
  const { visible, editable, newBooking, finalBookingTimePassed } = experienceInteractionConfig;

  const { profile } = useContext(AppContext);

  const [openClass, setOpenClass] = useState(0);
  const [openDeletePopup, setOpenDeletePopup] = useState(null);

  const classDetailsFormikValues = formik.values?.[objectName] || [];

  const classDetailRef = useRef(null);

  useEffect(() => {
    if (openClassIndex) {
      setOpenClass(openClassIndex);
    }
  }, [openClassIndex]);

  return (
    <div className="w-full flex flex-col gap-6">
      {classDetailsFormikValues.map((classDetail, index) => {
        return (
          <div
            key={index}
            ref={classDetailRef}
            id={`class-${index}`}
            className="w-full overflow-hidden rounded-lg border-2 border-red-10">
            <div
              className="w-full px-2 py-3 bg-red-10/30 relative text-center cursor-pointer user-select-none"
              onClick={() => {
                if (openClass === index) {
                  setOpenClass(null);
                } else {
                  setOpenClass(index);
                  scrollToClass(`class-${index}`);
                }
              }}>
              <span className="absolute top-0 left-0 h-full flex items-center justify-center px-4">
                <OpenIcon
                  className={`transition-transform duration-300 ${
                    openClass === index ? '' : 'rotate-180'
                  }`}
                />
              </span>
              {!profile?.loggedIn && (
                <span
                  className="absolute top-0 right-0 h-full flex items-center justify-center px-4"
                  onClick={(e) => {
                    e.stopPropagation();

                    setOpenDeletePopup(index);
                  }}>
                  <BiTrash className="text-red-10" />
                </span>
              )}
              <span
                className="text-red-10 text-xs"
                dangerouslySetInnerHTML={{
                  __html:
                    type === 'school'
                      ? t(`${translationPrefix}.classDetailsForm.title`) + ' ' + (index + 1)
                      : type === 'other-school'
                        ? t(`${translationPrefix}.classDetailsForm.title`) + ' ' + (index + 1)
                        : t(`${translationPrefix}.classDetailsForm.titleGroup`) + ' ' + (index + 1),
                }}
              />
            </div>
            {openClass === index && (
              <div className="w-full px-6 py-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1">
                    <Input
                      id={`${objectName}.${index}.name`}
                      name={`${objectName}.${index}.name`}
                      label={t(`${translationPrefix}.classDetailsForm.name.label`)}
                      placeholder={t(`${translationPrefix}.classDetailsForm.name.placeholder`)}
                      defaultValue={classDetail.name}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['name'] &&
                        formik.errors[`${objectName}`]?.[index]?.['name']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(`${objectName}.${index}.name`, e.target.value);
                      }}
                    />
                  </div>
                  {type !== 'other-group' && (
                    <div className="col-span-1">
                      {type === 'school' && (
                        <div className="flex w-full flex-col gap-4">
                          <Input
                            type={'select'}
                            options={classLevels}
                            id={`${objectName}.${index}.level`}
                            name={`${objectName}.${index}.level`}
                            label={t(`${translationPrefix}.classDetailsForm.level.label`)}
                            placeholder={t(
                              `${translationPrefix}.classDetailsForm.level.placeholder`,
                            )}
                            defaultValue={classDetail.level}
                            error={
                              formik.touched?.[`${objectName}`]?.[index]?.['level'] &&
                              formik.errors[`${objectName}`]?.[index]?.['level']
                            }
                            onChange={(e) => {
                              formik.setFieldValue(`${objectName}.${index}.level`, e.target.value);

                              // For validation schema only
                              formik.setFieldValue(
                                `${objectName}.${index}.experienceDetails.level`,
                                e.target.value,
                              );

                              formik.setFieldValue(
                                `${objectName}.${index}.experienceDetails.tourType`,
                                '',
                              );

                              formik.setFieldValue(
                                `${objectName}.${index}.experienceDetails.guidedTourType`,
                                '',
                              );

                              formik.setFieldValue(
                                `${objectName}.${index}.experienceDetails.timeSlot`,
                                '',
                              );

                              formik.setFieldValue(
                                `${objectName}.${index}.experienceDetails.professionId`,
                                '',
                              );
                            }}
                          />

                          {classDetail.level === 'other' && (
                            <Input
                              type={'text'}
                              id={`${objectName}.${index}.otherLevel`}
                              name={`${objectName}.${index}.otherLevel`}
                              label={t(`${translationPrefix}.classDetailsForm.otherLevel.label`)}
                              placeholder={t(
                                `${translationPrefix}.classDetailsForm.otherLevel.placeholder`,
                              )}
                              defaultValue={classDetail.otherLevel}
                              error={
                                formik.touched?.[`${objectName}`]?.[index]?.['otherLevel'] &&
                                formik.errors[`${objectName}`]?.[index]?.['otherLevel']
                              }
                              onChange={(e) => {
                                formik.setFieldValue(
                                  `${objectName}.${index}.otherLevel`,
                                  e.target.value,
                                );
                              }}
                            />
                          )}
                        </div>
                      )}
                      {type === 'other-school' && (
                        <Input
                          type={'text'}
                          id={`${objectName}.${index}.level`}
                          name={`${objectName}.${index}.level`}
                          label={t(`${translationPrefix}.classDetailsForm.level.label`)}
                          placeholder={t(`${translationPrefix}.classDetailsForm.level.placeholder`)}
                          defaultValue={classDetail.level}
                          error={
                            formik.touched?.[`${objectName}`]?.[index]?.['level'] &&
                            formik.errors[`${objectName}`]?.[index]?.['level']
                          }
                          onChange={(e) => {
                            formik.setFieldValue(`${objectName}.${index}.level`, e.target.value);
                          }}
                        />
                      )}
                    </div>
                  )}
                  <div className="col-span-1">
                    <Input
                      type={'select'}
                      options={Array.from({ length: 50 }, (_, i) => ({
                        value: i + 1,
                        label: i + 1,
                      }))}
                      id={`${objectName}.${index}.numberOfStudents`}
                      name={`${objectName}.${index}.numberOfStudents`}
                      label={t(`${translationPrefix}.classDetailsForm.numberOfStudents.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.numberOfStudents.placeholder`,
                      )}
                      defaultValue={classDetail.numberOfStudents}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['numberOfStudents'] &&
                        formik.errors[`${objectName}`]?.[index]?.['numberOfStudents']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(
                          `${objectName}.${index}.numberOfStudents`,
                          e.target.value,
                        );
                      }}
                    />
                  </div>
                  {type !== 'other-group' && (
                    <div className="col-span-1">
                      <Input
                        id={`${objectName}.${index}.sector`}
                        name={`${objectName}.${index}.sector`}
                        label={t(`${translationPrefix}.classDetailsForm.sector.label`)}
                        placeholder={t(`${translationPrefix}.classDetailsForm.sector.placeholder`)}
                        defaultValue={classDetail.sector}
                        error={
                          formik.touched?.[`${objectName}`]?.[index]?.['sector'] &&
                          formik.errors[`${objectName}`]?.[index]?.['sector']
                        }
                        onChange={(e) => {
                          formik.setFieldValue(`${objectName}.${index}.sector`, e.target.value);
                        }}
                      />
                    </div>
                  )}
                  <div className="col-span-1">
                    <Input
                      type={'radio'}
                      options={visitDays}
                      id={`${objectName}.${index}.visitDay`}
                      name={`${objectName}.${index}.visitDay`}
                      label={t(`${translationPrefix}.classDetailsForm.visitDay.label`)}
                      placeholder={t(`${translationPrefix}.classDetailsForm.visitDay.placeholder`)}
                      defaultValue={classDetail.visitDay}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['visitDay'] &&
                        formik.errors[`${objectName}`]?.[index]?.['visitDay']
                      }
                      disabled={newBooking ? false : !editable}
                      onChange={(value) => {
                        if (!newBooking && !editable) return;

                        formik.setFieldValue(`${objectName}.${index}.visitDay`, value);

                        if (value === 'sat') {
                          formik.setFieldValue(`${objectName}.${index}.bookExperience`, 'false');
                        }

                        formik.setFieldValue(
                          `${objectName}.${index}.experienceDetails.tourType`,
                          '',
                        );

                        formik.setFieldValue(
                          `${objectName}.${index}.experienceDetails.guidedTourType`,
                          '',
                        );

                        formik.setFieldValue(
                          `${objectName}.${index}.experienceDetails.timeSlot`,
                          '',
                        );

                        formik.setFieldValue(
                          `${objectName}.${index}.experienceDetails.professionId`,
                          '',
                        );
                      }}
                    />
                  </div>
                  <div className="col-span-1 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      type={'select'}
                      options={arrivalTimes[classDetail?.visitDay] || []}
                      id={`${objectName}.${index}.arrivalTime`}
                      name={`${objectName}.${index}.arrivalTime`}
                      label={t(`${translationPrefix}.classDetailsForm.arrivalTime.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.arrivalTime.placeholder`,
                      )}
                      defaultValue={classDetail.arrivalTime}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['arrivalTime'] &&
                        formik.errors[`${objectName}`]?.[index]?.['arrivalTime']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(`${objectName}.${index}.arrivalTime`, e.target.value);
                      }}
                    />
                    <Input
                      type={'select'}
                      options={departureTimes[classDetail?.visitDay] || []}
                      id={`${objectName}.${index}.departureTime`}
                      name={`${objectName}.${index}.departureTime`}
                      label={t(`${translationPrefix}.classDetailsForm.departureTime.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.departureTime.placeholder`,
                      )}
                      defaultValue={classDetail.departureTime}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['departureTime'] &&
                        formik.errors[`${objectName}`]?.[index]?.['departureTime']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(
                          `${objectName}.${index}.departureTime`,
                          e.target.value,
                        );
                      }}
                    />
                  </div>
                  <div className="col-span-1 flex w-full flex-col gap-4">
                    <Input
                      type={'select'}
                      options={modesOfTransport}
                      id={`${objectName}.${index}.modeOfTransport`}
                      name={`${objectName}.${index}.modeOfTransport`}
                      label={t(`${translationPrefix}.classDetailsForm.modeOfTransport.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.modeOfTransport.placeholder`,
                      )}
                      defaultValue={classDetail.modeOfTransport}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['modeOfTransport'] &&
                        formik.errors[`${objectName}`]?.[index]?.['modeOfTransport']
                      }
                      onChange={(e) => {
                        formik.setFieldValue(
                          `${objectName}.${index}.modeOfTransport`,
                          e.target.value,
                        );
                      }}
                    />

                    {classDetail.modeOfTransport === 'other' && (
                      <Input
                        type={'text'}
                        id={`${objectName}.${index}.otherModeOfTransport`}
                        name={`${objectName}.${index}.otherModeOfTransport`}
                        label={t(
                          `${translationPrefix}.classDetailsForm.otherModeOfTransport.label`,
                        )}
                        placeholder={t(
                          `${translationPrefix}.classDetailsForm.otherModeOfTransport.placeholder`,
                        )}
                        defaultValue={classDetail.otherModeOfTransport}
                        error={
                          formik.touched?.[`${objectName}`]?.[index]?.['otherModeOfTransport'] &&
                          formik.errors[`${objectName}`]?.[index]?.['otherModeOfTransport']
                        }
                        onChange={(e) => {
                          formik.setFieldValue(
                            `${objectName}.${index}.otherModeOfTransport`,
                            e.target.value,
                          );
                        }}
                      />
                    )}
                  </div>
                  <div className="col-span-1">
                    <Input
                      type={'radio'}
                      options={[
                        {
                          value: 'false',
                          label: t(`${translationPrefix}.classDetailsForm.picnicOnSite.false`),
                        },
                        {
                          value: 'true',
                          label: t(`${translationPrefix}.classDetailsForm.picnicOnSite.true`),
                        },
                      ]}
                      id={`${objectName}.${index}.picnicOnSite`}
                      name={`${objectName}.${index}.picnicOnSite`}
                      paragraphLabel={t(`${translationPrefix}.classDetailsForm.picnicOnSite.label`)}
                      placeholder={t(
                        `${translationPrefix}.classDetailsForm.picnicOnSite.placeholder`,
                      )}
                      defaultValue={classDetail.picnicOnSite}
                      error={
                        formik.touched?.[`${objectName}`]?.[index]?.['picnicOnSite'] &&
                        formik.errors[`${objectName}`]?.[index]?.['picnicOnSite']
                      }
                      onChange={(value) => {
                        formik.setFieldValue(`${objectName}.${index}.picnicOnSite`, value);
                      }}
                    />
                  </div>
                </div>

                <>
                  <Text
                    className="mt-6 w-full text-base text-gray-10 text-center"
                    type={'medium'}
                    text={t(`${translationPrefix}.classDetailsForm.accessibilitiesText`)}
                  />
                  <Text
                    className="mb-6 w-full text-gray-10 text-center"
                    type={'xsmall'}
                    text={t(`${translationPrefix}.classDetailsForm.accessibilitiesTextDescription`)}
                  />

                  <Input
                    type={'radio'}
                    options={[
                      {
                        value: 'false',
                        label: t(`${translationPrefix}.classDetailsForm.handicap.false`),
                      },
                      {
                        value: 'true',
                        label: t(`${translationPrefix}.classDetailsForm.handicap.true`),
                      },
                    ]}
                    id={`${objectName}.${index}.handicap`}
                    name={`${objectName}.${index}.handicap`}
                    paragraphLabel={t(`${translationPrefix}.classDetailsForm.handicap.label`)}
                    paragraphLabelClassName="text-center"
                    radioClassName="justify-center"
                    placeholder={t(`${translationPrefix}.classDetailsForm.handicap.placeholder`)}
                    defaultValue={classDetail.handicap}
                    error={
                      formik.touched?.[`${objectName}`]?.[index]?.['handicap'] &&
                      formik.errors[`${objectName}`]?.[index]?.['handicap']
                    }
                    onChange={(value) => {
                      formik.setFieldValue(`${objectName}.${index}.handicap`, value);
                    }}
                  />

                  {classDetail.handicap === 'true' && (
                    <>
                      <Input
                        type={'textarea'}
                        id={`${objectName}.${index}.handicapDescription`}
                        name={`${objectName}.${index}.handicapDescription`}
                        inputClassName="block mt-2 md:max-w-md mx-auto"
                        placeholder={t(
                          `${translationPrefix}.classDetailsForm.handicapDescription.placeholder`,
                        )}
                        defaultValue={classDetail.handicapDescription}
                        error={
                          formik.touched?.[`${objectName}`]?.[index]?.['handicapDescription'] &&
                          formik.errors[`${objectName}`]?.[index]?.['handicapDescription']
                        }
                        errorClassName="text-center"
                        onChange={(e) => {
                          formik.setFieldValue(
                            `${objectName}.${index}.handicapDescription`,
                            e.target.value,
                          );
                        }}
                      />
                      <Text
                        className="mt-6 w-full text-xs text-gray-10 text-center"
                        type={'xsmall'}
                        text={t(`${translationPrefix}.classDetailsForm.accessibilitiesTextFooter`)}
                      />
                    </>
                  )}
                </>

                <span className="block my-10 bg-black h-[0.5px] w-full" />

                <>
                  <Text
                    className="mt-6 w-full text-base text-gray-10 text-center"
                    type={'medium'}
                    text={t(`${translationPrefix}.classDetailsForm.additionalInformationHeading`)}
                  />
                  <Text
                    className="mb-6 w-full text-gray-10 text-center"
                    type={'xsmall'}
                    text={t(
                      `${translationPrefix}.classDetailsForm.additionalInformationSubHeading`,
                    )}
                  />

                  <div className="mt-6">
                    <Text
                      className="w-full text-base text-gray-10 text-center"
                      type={'medium'}
                      text={t(`${translationPrefix}.classDetailsForm.responsibleDetailsHeading`)}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      <div className="col-span-1">
                        <Input
                          type={'text'}
                          id={`${objectName}.${index}.responsibleInformation.firstName`}
                          name={`${objectName}.${index}.responsibleInformation.firstName`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsFirstName.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsFirstName.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.firstName}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'firstName'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.firstName`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type={'text'}
                          id={`${objectName}.${index}.responsibleInformation.lastName`}
                          name={`${objectName}.${index}.responsibleInformation.lastName`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsLastName.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsLastName.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.lastName}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'lastName'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.lastName`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>

                      <div className="col-span-1">
                        <Input
                          type={'text'}
                          id={`${objectName}.${index}.responsibleInformation.function`}
                          name={`${objectName}.${index}.responsibleInformation.function`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsFunction.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsFunction.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.function}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'function'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.function`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type={'email'}
                          id={`${objectName}.${index}.responsibleInformation.email`}
                          name={`${objectName}.${index}.responsibleInformation.email`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsEmail.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsEmail.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.email}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'email'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.email`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          type={'tel'}
                          id={`${objectName}.${index}.responsibleInformation.phone`}
                          name={`${objectName}.${index}.responsibleInformation.phone`}
                          label={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsPhone.label`,
                          )}
                          placeholder={t(
                            `${translationPrefix}.classDetailsForm.responsibleDetailsPhone.placeholder`,
                          )}
                          defaultValue={classDetail?.responsibleInformation?.phone}
                          error={
                            formik.errors[`${objectName}`]?.[index]?.['responsibleInformation']?.[
                              'phone'
                            ]
                          }
                          onChange={(e) => {
                            formik.setFieldValue(
                              `${objectName}.${index}.responsibleInformation.phone`,
                              e.target.value,
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>

                {visible &&
                  type === 'school' &&
                  classDetail.visitDay &&
                  classDetail.visitDay !== 'sat' && (
                    <>
                      <span className="block my-10 bg-black h-[0.5px] w-full" />

                      <Input
                        type={'radio'}
                        options={[
                          {
                            value: 'false',
                            label: t(`${translationPrefix}.classDetailsForm.bookExperience.false`),
                          },
                          {
                            value: 'true',
                            label: t(`${translationPrefix}.classDetailsForm.bookExperience.true`),
                          },
                        ]}
                        id={`${objectName}.${index}.bookExperience`}
                        name={`${objectName}.${index}.bookExperience`}
                        paragraphLabel={t(
                          `${translationPrefix}.classDetailsForm.bookExperience.label`,
                        )}
                        paragraphLabelClassName="text-center"
                        radioClassName="justify-center"
                        defaultValue={classDetail.bookExperience}
                        error={
                          formik.touched?.[`${objectName}`]?.[index]?.['bookExperience'] &&
                          formik.errors[`${objectName}`]?.[index]?.['bookExperience']
                        }
                        disabled={!editable}
                        onChange={(value) => {
                          if (!editable) return;

                          formik.setFieldValue(`${objectName}.${index}.bookExperience`, value);

                          // Reset all experience details fields
                          formik.setFieldValue(
                            `${objectName}.${index}.experienceDetails.timeSlot`,
                            undefined,
                          );
                          formik.setFieldValue(
                            `${objectName}.${index}.experienceDetails.tourType`,
                            undefined,
                          );
                          formik.setFieldValue(
                            `${objectName}.${index}.experienceDetails.guidedTourType`,
                            undefined,
                          );
                          formik.setFieldValue(
                            `${objectName}.${index}.experienceDetails.professionId`,
                            undefined,
                          );

                          // Clear validation errors for experienceDetails when bookExperience is false
                          if (value === 'false') {
                            setTimeout(() => {
                              // Clear errors for experienceDetails and its nested fields
                              const currentErrors = { ...formik.errors };
                              if (
                                currentErrors[objectName] &&
                                currentErrors[objectName][index] &&
                                currentErrors[objectName][index].experienceDetails
                              ) {
                                delete currentErrors[objectName][index].experienceDetails;
                                formik.setErrors(currentErrors);
                              }

                              // Clear touched state for experienceDetails
                              const currentTouched = { ...formik.touched };
                              if (
                                currentTouched[objectName] &&
                                currentTouched[objectName][index] &&
                                currentTouched[objectName][index].experienceDetails
                              ) {
                                delete currentTouched[objectName][index].experienceDetails;
                                formik.setTouched(currentTouched);
                              }
                            }, 0);
                          }
                        }}
                      />

                      {classDetail.bookExperience === 'true' && (
                        <div className="mt-6">
                          <Input
                            type={'select'}
                            options={tourTypes}
                            id={`${objectName}.${index}.experienceDetails.tourType`}
                            name={`${objectName}.${index}.experienceDetails.tourType`}
                            label={t(`${translationPrefix}.classDetailsForm.tourType.label`)}
                            labelClassName="text-center"
                            inputClassName="block sm:max-w-md mx-auto"
                            placeholder={t(
                              `${translationPrefix}.classDetailsForm.tourType.placeholder`,
                            )}
                            errorClassName="text-center"
                            defaultValue={classDetail.experienceDetails.tourType}
                            error={
                              formik.touched?.[`${objectName}`]?.[index]?.['experienceDetails']?.[
                                'tourType'
                              ] &&
                              formik.errors[`${objectName}`]?.[index]?.['experienceDetails']?.[
                                'tourType'
                              ]
                            }
                            disabled={!editable}
                            onChange={(e) => {
                              if (!editable) return;

                              formik.setFieldValue(
                                `${objectName}.${index}.experienceDetails.tourType`,
                                e.target.value,
                              );

                              // reset selected time slot
                              formik.setFieldValue(
                                `${objectName}.${index}.experienceDetails.timeSlot`,
                                undefined,
                              );
                            }}
                          />

                          {classDetail.experienceDetails.tourType && (
                            <TimeSlotsForm
                              visitDay={classDetail.visitDay}
                              tourType={classDetail.experienceDetails.tourType}
                              timeSlot={classDetail.experienceDetails.timeSlot}
                              changeTimeSlot={(value) => {
                                if (!editable) return;

                                formik.setFieldValue(
                                  `${objectName}.${index}.experienceDetails.timeSlot`,
                                  value,
                                );
                                formik.setFieldValue(
                                  `${objectName}.${index}.experienceDetails.guidedTourType`,
                                  undefined,
                                );
                                formik.setFieldValue(
                                  `${objectName}.${index}.experienceDetails.professionId`,
                                  undefined,
                                );
                                // First validate then set touched state
                                // This ensures errors are cleared properly
                                setTimeout(() => {
                                  formik.validateField(
                                    `${objectName}.${index}.experienceDetails.timeSlot`,
                                  );
                                  formik.setFieldTouched(
                                    `${objectName}.${index}.experienceDetails.timeSlot`,
                                    true,
                                  );
                                }, 0);
                              }}
                              guidedTourType={classDetail.experienceDetails.guidedTourType}
                              professionId={classDetail.experienceDetails.professionId}
                              changeGuidedTourType={(value) => {
                                if (!editable) return;

                                formik.setFieldValue(
                                  `${objectName}.${index}.experienceDetails.guidedTourType`,
                                  value,
                                );
                                formik.setFieldValue(
                                  `${objectName}.${index}.experienceDetails.professionId`,
                                  undefined,
                                );
                                // First validate then set touched state
                                setTimeout(() => {
                                  formik.validateField(
                                    `${objectName}.${index}.experienceDetails.guidedTourType`,
                                  );
                                  formik.setFieldTouched(
                                    `${objectName}.${index}.experienceDetails.guidedTourType`,
                                    true,
                                  );
                                }, 0);
                              }}
                              changeProfessionId={(value) => {
                                if (!editable) return;

                                formik.setFieldValue(
                                  `${objectName}.${index}.experienceDetails.professionId`,
                                  value,
                                );
                                // First validate then set touched state
                                setTimeout(() => {
                                  formik.validateField(
                                    `${objectName}.${index}.experienceDetails.professionId`,
                                  );
                                  formik.setFieldTouched(
                                    `${objectName}.${index}.experienceDetails.professionId`,
                                    true,
                                  );
                                }, 0);
                              }}
                              disabled={!editable}
                              error={
                                formik.touched?.[`${objectName}`]?.[index]?.['experienceDetails']?.[
                                  'timeSlot'
                                ] &&
                                formik.errors[`${objectName}`]?.[index]?.['experienceDetails']?.[
                                  'timeSlot'
                                ]
                              }
                              translationPrefix={translationPrefix}
                              classDetailRef={classDetailRef}
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}

                {!finalBookingTimePassed &&
                  classDetail?.experienceDetails?.tourType &&
                  classDetail?.numberOfStudents <
                    NUMBER_OF_TOUR_STUDENTS[classDetail.experienceDetails.tourType].MIN && (
                    <p className="error-field text-red-20 text-xl font-semibold mt-6 text-center animate-bounce">
                      {t(
                        `${translationPrefix}.classDetailsForm.${classDetail.experienceDetails.tourType}.numberOfStudents.min`,
                      )}
                    </p>
                  )}

                {!finalBookingTimePassed &&
                  classDetail?.experienceDetails?.tourType &&
                  classDetail?.numberOfStudents >
                    NUMBER_OF_TOUR_STUDENTS[classDetail.experienceDetails.tourType].MAX && (
                    <p className="error-field text-red-20 text-xl font-semibold mt-6 text-center animate-bounce">
                      {t(
                        `${translationPrefix}.classDetailsForm.${classDetail.experienceDetails.tourType}.numberOfStudents.max`,
                      )}
                    </p>
                  )}

                <div
                  className="mt-8 text-center"
                  dangerouslySetInnerHTML={{
                    __html: t(`${translationPrefix}.classDetailsForm.bottomText`),
                  }}
                />
              </div>
            )}
          </div>
        );
      })}

      <Popup
        open={openDeletePopup !== null}
        closePopup={() => setOpenDeletePopup(null)}
        confirmationPopup={{
          heading: t(`${translationPrefix}.classDetailsForm.deleteClass.heading`),
          subHeading: t(`${translationPrefix}.classDetailsForm.deleteClass.subHeading`),
          cancelButtonText: t(`${translationPrefix}.classDetailsForm.deleteClass.cancelButtonText`),
          cancelButtonCallback: () => {
            setOpenDeletePopup(null);
          },
          confirmButtonText: t(
            `${translationPrefix}.classDetailsForm.deleteClass.confirmButtonText`,
          ),
          confirmButtonCallback: () => {
            formik.setFieldValue(
              'classes',
              classDetailsFormikValues.filter((_, i) => i !== openDeletePopup),
            );
            setOpenDeletePopup(null);
          },
        }}
      />
    </div>
  );
};

export default ClassDetailsForm;
